import waitUntil from 'wait-until-promise';
import { CONFIG } from '../../../config';
import {
  isGa4Loaded,
  isAnalyticsConsentGranted,
  getConsentResolvedVia,
  getConsentResolutionWaitMs
} from './ga4State';

const measurementId = CONFIG.GA4_MEASUREMENT_ID;

type GA4Ids = {
  ga4_session_id?: number;
  ga4_session_number?: number;
  ga4_client_id?: string;
};

/**
 * Why each id could be missing, ordered so the first applicable reason is the
 * one worth acting on (e.g. no point reporting a script-load timeout if
 * consent was never granted in the first place).
 */
export type GA4IdsMissingReason =
  | 'ids_present'
  | 'measurement_id_not_configured'
  | 'consent_not_granted'
  | 'script_not_loaded'
  | 'window_ga4_not_present'
  | 'ids_not_returned_after_load'
  | 'partial_ids_missing';

export type GA4IdsDiagnostics = {
  ga4_measurement_id_configured: boolean;
  ga4_consent_granted_at_call_time: boolean;
  ga4_consent_resolved_via: string | undefined;
  ga4_consent_resolution_wait_ms: number | undefined;
  ga4_script_loaded: boolean;
  ga4_script_load_wait_ms: number;
  ga4_window_object_present: boolean;
  ga4_ids_fetch_wait_ms: number;
  ga4_session_id_present: boolean;
  ga4_session_number_present: boolean;
  ga4_client_id_present: boolean;
  ga4_ids_missing_reason: GA4IdsMissingReason;
};

export type GA4IdsResult = GA4Ids & { diagnostics: GA4IdsDiagnostics };

const GET_GA4_IDS_TIMEOUT = 2000;
const GA4_READY_MAX_WAIT = 3000;
const GA4_READY_CHECK_DELAY = 50;

const computeMissingReason = (
  diagnostics: Omit<GA4IdsDiagnostics, 'ga4_ids_missing_reason'>
): GA4IdsMissingReason => {
  const allIdsPresent =
    diagnostics.ga4_session_id_present &&
    diagnostics.ga4_session_number_present &&
    diagnostics.ga4_client_id_present;
  if (allIdsPresent) return 'ids_present';
  if (!diagnostics.ga4_measurement_id_configured) return 'measurement_id_not_configured';
  if (!diagnostics.ga4_consent_granted_at_call_time) return 'consent_not_granted';
  if (!diagnostics.ga4_script_loaded) return 'script_not_loaded';
  if (!diagnostics.ga4_window_object_present) return 'window_ga4_not_present';
  const noIdsAtAll =
    !diagnostics.ga4_session_id_present &&
    !diagnostics.ga4_session_number_present &&
    !diagnostics.ga4_client_id_present;
  return noIdsAtAll ? 'ids_not_returned_after_load' : 'partial_ids_missing';
};

export const getGA4Ids = async (): Promise<GA4IdsResult> => {
  const ids: GA4Ids = {};
  const consentGrantedAtCallTime = isAnalyticsConsentGranted();
  const consentResolvedVia = getConsentResolvedVia();
  const consentResolutionWaitMs = getConsentResolutionWaitMs();

  if (!measurementId) {
    const diagnosticsWithoutReason = {
      ga4_measurement_id_configured: false,
      ga4_consent_granted_at_call_time: consentGrantedAtCallTime,
      ga4_consent_resolved_via: consentResolvedVia,
      ga4_consent_resolution_wait_ms: consentResolutionWaitMs,
      ga4_script_loaded: isGa4Loaded(),
      ga4_script_load_wait_ms: 0,
      ga4_window_object_present: window.ga4 != null,
      ga4_ids_fetch_wait_ms: 0,
      ga4_session_id_present: false,
      ga4_session_number_present: false,
      ga4_client_id_present: false
    };
    const diagnostics: GA4IdsDiagnostics = {
      ...diagnosticsWithoutReason,
      ga4_ids_missing_reason: computeMissingReason(diagnosticsWithoutReason)
    };
    return { ...ids, diagnostics };
  }

  const loadWaitStart = performance.now();
  await waitUntil(isGa4Loaded, GA4_READY_MAX_WAIT, GA4_READY_CHECK_DELAY).catch(() => {});
  const scriptLoaded = isGa4Loaded();
  const scriptLoadWaitMs = Math.round(performance.now() - loadWaitStart);
  const ga4 = window.ga4;

  if (!scriptLoaded || ga4 == null) {
    const diagnosticsWithoutReason = {
      ga4_measurement_id_configured: true,
      ga4_consent_granted_at_call_time: consentGrantedAtCallTime,
      ga4_consent_resolved_via: consentResolvedVia,
      ga4_consent_resolution_wait_ms: consentResolutionWaitMs,
      ga4_script_loaded: scriptLoaded,
      ga4_script_load_wait_ms: scriptLoadWaitMs,
      ga4_window_object_present: ga4 != null,
      ga4_ids_fetch_wait_ms: 0,
      ga4_session_id_present: false,
      ga4_session_number_present: false,
      ga4_client_id_present: false
    };
    const diagnostics: GA4IdsDiagnostics = {
      ...diagnosticsWithoutReason,
      ga4_ids_missing_reason: computeMissingReason(diagnosticsWithoutReason)
    };
    return { ...ids, diagnostics };
  }

  const idsFetchStart = performance.now();

  await new Promise<void>(resolve => {
    const areAllIdsPresent = () =>
      ids.ga4_session_id != null && ids.ga4_session_number != null && ids.ga4_client_id != null;

    ga4(
      'get',
      measurementId,
      'session_id',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      session_id => {
        ids.ga4_session_id = typeof session_id === 'number' ? session_id : Number(session_id);
        if (areAllIdsPresent()) resolve();
      }
    );
    ga4(
      'get',
      measurementId,
      'session_number',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      session_number => {
        ids.ga4_session_number =
          typeof session_number === 'number' ? session_number : Number(session_number);
        if (areAllIdsPresent()) resolve();
      }
    );
    ga4(
      'get',
      measurementId,
      'client_id',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      client_id => {
        ids.ga4_client_id = String(client_id);
        if (areAllIdsPresent()) resolve();
      }
    );

    setTimeout(resolve, GET_GA4_IDS_TIMEOUT);
  });

  const diagnosticsWithoutReason = {
    ga4_measurement_id_configured: true,
    ga4_consent_granted_at_call_time: consentGrantedAtCallTime,
    ga4_consent_resolved_via: consentResolvedVia,
    ga4_consent_resolution_wait_ms: consentResolutionWaitMs,
    ga4_script_loaded: scriptLoaded,
    ga4_script_load_wait_ms: scriptLoadWaitMs,
    ga4_window_object_present: true,
    ga4_ids_fetch_wait_ms: Math.round(performance.now() - idsFetchStart),
    ga4_session_id_present: ids.ga4_session_id != null && !Number.isNaN(ids.ga4_session_id),
    ga4_session_number_present:
      ids.ga4_session_number != null && !Number.isNaN(ids.ga4_session_number),
    ga4_client_id_present: ids.ga4_client_id != null
  };

  const diagnostics: GA4IdsDiagnostics = {
    ...diagnosticsWithoutReason,
    ga4_ids_missing_reason: computeMissingReason(diagnosticsWithoutReason)
  };

  return { ...ids, diagnostics };
};
